// Per-company branding and locale. Everything a new deployment needs to change
// about how the dashboard LOOKS lives here, read from Vite env vars
// (frontend/.env.production — see .env.example). No other file should hardcode
// a company name, logo, link, accent colour or timezone.

const env = import.meta.env;
const val = (k, fallback = "") => {
  const v = env[k];
  return typeof v === "string" && v.trim() !== "" ? v.trim() : fallback;
};

export const BRAND_NAME = val("VITE_BRAND_NAME", "Company");
// Path under frontend/public/ (e.g. "/logo.png") or a full URL. Empty → the
// header shows BRAND_NAME as a text wordmark instead.
export const BRAND_LOGO = val("VITE_BRAND_LOGO");
// Shown under the login title, e.g. "example.com accounting audit & monitoring".
export const BRAND_TAGLINE = val("VITE_BRAND_TAGLINE", "Accounting audit & monitoring");
// Optional "Powered by" credit in the footer. Leave the name empty to hide it.
export const POWERED_BY_NAME = val("VITE_POWERED_BY_NAME");
export const POWERED_BY_URL = val("VITE_POWERED_BY_URL");

// Every clock reading on the page is shown in ONE timezone and labelled,
// rather than left to the viewer's machine — a reconciliation checked against
// the company's business day should not read differently on a laptop that is
// travelling. Billing DATES stay UTC regardless (see formatBillingDate).
export const DISPLAY_TIMEZONE = val("VITE_DISPLAY_TIMEZONE", "Asia/Manila");
export const DISPLAY_TIMEZONE_LABEL = val("VITE_DISPLAY_TIMEZONE_LABEL", shortZoneName(DISPLAY_TIMEZONE));

function shortZoneName(tz) {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    return part ? part.value : tz;
  } catch {
    return tz;
  }
}

// Brand colours. CSS defaults live in index.css; any of these set in .env
// override them at startup.
//   --accent         the bright brand fill: selection bars, active nav, focus
//                    ring. Never used for text on white (it rarely passes AA).
//   --accent-strong  filled buttons and the daily-spend bars; carries white
//                    text, so it must reach 4.5:1 against #fff.
//   --accent-hover   hover state of --accent-strong.
//   --accent-soft    pale tint for selected rows and segments.
//   --accent-text    links and thin borders; must pass 4.5:1 on white AND on
//                    --accent-soft.
//   --chrome         the dark brand surface behind the header and the sign-in
//                    panel. Text on it is white, so keep it dark.
const BRAND_VARS = {
  "--accent": "VITE_BRAND_ACCENT",
  "--accent-strong": "VITE_BRAND_ACCENT_STRONG",
  "--accent-hover": "VITE_BRAND_ACCENT_HOVER",
  "--accent-soft": "VITE_BRAND_ACCENT_SOFT",
  "--accent-text": "VITE_BRAND_ACCENT_TEXT",
  "--chrome": "VITE_BRAND_CHROME",
};

// A dark, single-colour logo disappears on --chrome. "true" renders it white
// there (CSS filter), so the company's own file can be used unedited.
export const BRAND_LOGO_INVERT = val("VITE_BRAND_LOGO_INVERT").toLowerCase() === "true";

// Google Fonts family names, e.g. "Roboto" and "Raleway". Empty = system UI
// font. Figures always use the body font with tabular numerals, so a display
// face with old-style figures can be used for headings without hurting tables.
const FONT_BODY = val("VITE_BRAND_FONT_BODY");
const FONT_HEADING = val("VITE_BRAND_FONT_HEADING");

function loadFonts() {
  const families = [
    FONT_BODY && `family=${encodeURIComponent(FONT_BODY)}:wght@400;500;700`,
    FONT_HEADING && FONT_HEADING !== FONT_BODY && `family=${encodeURIComponent(FONT_HEADING)}:wght@600;700`,
  ].filter(Boolean);
  if (!families.length) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${families.join("&")}&display=swap`;
  document.head.appendChild(link);
}

export function applyBrand() {
  const root = document.documentElement;
  for (const [cssVar, envKey] of Object.entries(BRAND_VARS)) {
    const v = val(envKey);
    if (v) root.style.setProperty(cssVar, v);
  }
  const stack = (name) => `"${name}", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`;
  if (FONT_BODY) root.style.setProperty("--font", stack(FONT_BODY));
  if (FONT_HEADING) root.style.setProperty("--font-heading", stack(FONT_HEADING));
  loadFonts();
  document.title = `AWS Billing Dashboard — ${BRAND_NAME}`;
}
